import { io } from 'socket.io-client';
import { getuserdata, updateactivities } from '../services/apiService';


// Directly define your API URLs
const url = process.env.REACT_APP_MODE === 'LIVE' ? process.env.REACT_APP_API_BASE_LIVE_URL : process.env.REACT_APP_API_BASE_URL;
const API_BASE_URL = `${url}/api/realtime/`;
const API_URL = `${url}/api/`;


// Environment variables with fallbacks - defer error to runtime for SSR compatibility
const getDatabaseId = () => {
  const id = process.env.REACT_APP_DATABASEID;
  if (!id && typeof window === 'undefined') {
    // SSR context - use default or empty string
    console.warn('⚠️ REACT_APP_DATABASEID is missing in SSR context. Using default.');
    return '';
  }
  if (!id) {
    throw new Error('REACT_APP_DATABASEID is missing! Please add it to your .env file.');
  }
  return id;
};

const getApiKey = () => {
  const key = process.env.REACT_APP_API_KEY_ONEKLICKS;
  if (!key && typeof window === 'undefined') {
    // SSR context - use default or empty string
    console.warn('⚠️ REACT_APP_API_KEY_ONEKLICKS is missing in SSR context. Using default.');
    return '';
  }
  if (!key) {
    throw new Error('REACT_APP_API_KEY_ONEKLICKS is missing! Please add it to your .env file.');
  }
  return key;
};

// Store as getters instead of immediate values
const databaseid = getDatabaseId();
const API_key = getApiKey();

class OneClickSDK {
  constructor() {
    this.pollingInterval = null;
    this.socket = io(API_BASE_URL);
  }

  disconnectSocket() {
    if (this.socket) {
      this.socket.disconnect();
    }
  }

  async postRequest(endpoint, data) {
    try {
      console.log("postRequest starting time", new Date().toISOString());
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': API_key,
          'Cache-Control': 'no-store',
        },
        body: JSON.stringify(data),
      });

      if(response.status === 401 || response.status === 403 || response.status === 404 || response.status === 500){
        console.log('Error in postRequest',response);
        const errorData = await response.json();
        throw new Error(`API Error ${response.status}: ${errorData.message || 'Server error'}`);
      }
      
      console.log("postRequest ending time", new Date().toISOString());

      return await response.json();
    } catch (error) {
      throw new Error(`Error in ${endpoint}: ${error.message}`);
    }
  }

  // CRUD methods
  create(tableSchema, tableName, data, conditions) {
    return this.postRequest('dynamicCreate', { tableSchema, tableName, data, conditions, databaseid });
  }

  read(tableSchema, tableName, conditions, orderBy, search, pagination, margedata, range = []) {
    // const localuser = JSON.parse(localStorage.getItem("presentuser"));
    // console.log("localuser", localuser);
    // const localid = localuser.id || "";
    // const name = tableName;
    // const description = JSON.stringify(conditions);
    // const type = 'read';
    // updateactivities({localid, name, description, type });
    return this.postRequest('dynamicRead', { tableSchema, tableName, conditions, orderBy, search, pagination, margedata, range, databaseid });
  }

  update(tableSchema, tableName, data, condition) {
    return this.postRequest('dynamicUpdate', { tableSchema, tableName, data, condition, databaseid });
  }

  delete(tableSchema, tableName, condition) {
    return this.postRequest('dynamicDelete', { tableSchema, tableName, condition, databaseid });
  }

  count(tableSchema, tableName, conditions) {
    return this.postRequest('dynamicCount', { tableSchema, tableName, conditions, databaseid });
  }

  hashPassword(password) {
    return this.postRequest('hashPassword', { password });
  }

  startLongPolling(tableSchema, tableName, conditions, orderBy, search, callback, pagination, margedata) {
    if (typeof callback !== 'function') {
      throw new Error('Callback must be a function.');
    }

    const fetchData = async () => {
      try {
        const data = await this.read(tableSchema, tableName, conditions, orderBy, search, pagination, margedata);
        callback(data);
      } catch (error) {
        throw new Error(`Polling error: ${error.message}`);
      }
    };

    this.pollingInterval = setInterval(fetchData, 1000);
  }

  stopLongPolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  listenForUpdates(tableSchema, tableName, callback) {
    const eventName = `${tableSchema}_${tableName}_update`;

    this.socket.on(eventName, (data) => {
      if (typeof callback === 'function') {
        callback(data);
      }
    });
  }

  stopListeningForUpdates(tableSchema, tableName) {
    const eventName = `${tableSchema}_${tableName}_update`;
    this.socket.off(eventName);
  }
}

// Email sender utility
export const sendEmail = async (email, subject, text) => {
  const host = process.env.REACT_APP_SMTP_HOST || "mail.meddylist.com";
  const portEnv = process.env.REACT_APP_SMTP_PORT;
  const secureEnv = process.env.REACT_APP_SMTP_SECURE;

  const resolvedPort = portEnv ? Number(portEnv) : 465;
  const resolvedSecure = typeof secureEnv === 'string' ? secureEnv.toLowerCase() === 'true' : true;

  const smtpConfigPrimary = {
    host,
    port: resolvedPort,
    secure: resolvedSecure,
    auth: {
      user: process.env.REACT_APP_SMTP_USER || "info@meddylist.com",
      pass: process.env.REACT_APP_SMTP_PASS || ""
    }
  };

  // Fallback: try STARTTLS on 587 if implicit SSL 465 fails (or vice versa)
  const smtpConfigFallback = resolvedPort === 465
    ? { ...smtpConfigPrimary, port: 587, secure: false }
    : { ...smtpConfigPrimary, port: 465, secure: true };

  const api_key = process.env.REACT_APP_API_KEY_ONEKLICKS || (() => {
    throw new Error('REACT_APP_API_KEY_ONEKLICKS is missing! Please add it to your .env file.');
  })();

  try {
    const primaryResponse = await fetch(`${API_URL}email/send-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': API_key,
        'Cache-Control': 'no-store',
      },
      body: JSON.stringify({ smtpConfig: smtpConfigPrimary, email, subject, text, api_key }),
    });

    const primaryJson = await primaryResponse.json();
    if (primaryJson?.success === true) {
      return primaryJson;
    }

    // If connection refused or similar network error from backend, try fallback
    const shouldFallback = typeof primaryJson?.error === 'string' && /ECONNREFUSED|connect|timeout/i.test(primaryJson.error);
    if (!shouldFallback) {
      return primaryJson;
    }

    const fallbackResponse = await fetch(`${API_URL}email/send-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': API_key,
        'Cache-Control': 'no-store',
      },
      body: JSON.stringify({ smtpConfig: smtpConfigFallback, email, subject, text, api_key }),
    });
    return await fallbackResponse.json();
  } catch (error) {
    throw new Error(`Email sending error: ${error.message}`);
  }
};

export const sendSMS = async (phoneNumber, body) => {
  try {
    const response = await fetch(`${API_URL}email/send-sms`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': API_key,
        'Cache-Control': 'no-store',
      },
      body: JSON.stringify({ phoneNumber, body }),
    });

    return await response.json();
  } catch (error) {
    throw new Error(`SMS sending error: ${error.message}`);
  }
};

export const sendWhatsApp = async (phoneNumber, message, template = "whatsapp") => {
  try {
    console.log('Sending WhatsApp message to:', phoneNumber);
    console.log('Message:', message);
    
    // Clean phone number - remove any non-digit characters except + and ensure proper format
    let cleanPhoneNumber = phoneNumber.replace(/[^\d+]/g, '');
    
    // Remove spaces and ensure it starts with country code
    cleanPhoneNumber = cleanPhoneNumber.replace(/\s/g, '');
    
    // If it starts with 0, replace with +91 (India country code)
    if (cleanPhoneNumber.startsWith('0')) {
        cleanPhoneNumber = '+91' + cleanPhoneNumber.substring(1);
    }
    
    // If it doesn't start with +, add +91
    if (!cleanPhoneNumber.startsWith('+')) {
        cleanPhoneNumber = '+91' + cleanPhoneNumber;
    }
    
    console.log('Original phone:', phoneNumber);
    console.log('Cleaned phone:', cleanPhoneNumber);
    
    // Try to route through backend first (recommended approach)
    const backendUrl = process.env.REACT_APP_API_BASE_URL || 'https://apis.oneklicks.com';
    const backendWhatsAppUrl = `${backendUrl}/api/whatsapp/send`;
    
    try {
      const response = await fetch(backendWhatsAppUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': process.env.REACT_APP_API_KEY_ONEKLICKS || '',
        },
        body: JSON.stringify({
          number: cleanPhoneNumber,
          message: message,
          template: template
        }),
      });

      if (response.ok) {
        const result = await response.json();
        return {
          success: true,
          message: 'WhatsApp message sent successfully via backend',
          data: result
        };
      }
    } catch (backendError) {
      console.log('Backend WhatsApp route failed, trying direct API...', backendError);
    }
    
    // Use WhatsApp API to send message directly (open in new window/tab)
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://api.whatsapp.com/send/?phone=${cleanPhoneNumber}&text=${encodedMessage}&type=phone_number&app_absent=0`;
    
    console.log('Opening WhatsApp URL:', whatsappUrl);
    
    // Open WhatsApp in a new window/tab - this is more reliable than iframe
    const whatsappWindow = window.open(whatsappUrl, '_blank', 'width=600,height=700,scrollbars=yes,resizable=yes');
    
    // Check if window opened successfully
    if (whatsappWindow) {
      console.log('WhatsApp window opened successfully');
      
      // Return success for direct sending
      return {
        success: true,
        message: 'WhatsApp opened in new window!',
        data: {
          status: 'opened',
          url: whatsappUrl,
          note: 'WhatsApp opened in new window/tab. Please send the message manually.'
        }
      };
    } else {
      // Fallback: try to open in same window
      console.log('Failed to open new window, trying same window...');
      window.location.href = whatsappUrl;
      
      return {
        success: true,
        message: 'WhatsApp opened!',
        data: {
          status: 'redirected',
          url: whatsappUrl,
          note: 'Redirected to WhatsApp. Please send the message manually.'
        }
      };
    }

    
  } catch (error) {
    console.error('WhatsApp sending error:', error);
    
    // Check if it's a CORS error and provide helpful message
    if (error.message.includes('Failed to fetch')) {
      throw new Error('CORS/Network error: WhatsApp API cannot be accessed directly from browser. Please contact administrator to set up backend proxy.');
    }
    
    throw new Error(`WhatsApp sending error: ${error.message}`);
  }
};

export const callAiApi = async (message) => {
  try {
    const response = await fetch(`http://27.7.41.165:11434/api/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        model: "meddylist-ai",
        prompt: message,
        stream: true
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed with status ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let result = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      const chunk = decoder.decode(value, { stream: true });
      console.log(chunk); // You can update UI here letter by letter
      result += chunk;
    }

    return result;
  } catch (error) {
    console.error('Error:', error.message);
    throw error;
  }
};



export const oneKlickstest = new OneClickSDK();
